import { Loader2, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import type { ApiKey, ApiKeyWithOwner } from "@/api/generated/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SCOPE_GROUPS } from "@/hooks/use-api-key-settings";

function formatDate(value?: Date | string | null): string {
  if (!value) return "Never";
  return new Date(value).toLocaleDateString();
}

type ScopeSummaryItem = { label: string; access: string };

/** Collapse raw scopes into per-domain entries using the same labels/order as the create form. */
function summarizeScopes(scopes: ApiKey["scopes"]): ScopeSummaryItem[] {
  const owned = new Set<string>(scopes);
  const items: ScopeSummaryItem[] = [];
  const covered = new Set<string>();

  for (const group of SCOPE_GROUPS) {
    const readScopes = group.readScopes ?? [group.read];
    const writeScopes = group.writeScopes ?? (group.write ? [group.write] : []);
    const hasRead = readScopes.some((scope) => owned.has(scope));
    const hasWrite = writeScopes.some((scope) => owned.has(scope));
    if (!hasRead && !hasWrite) continue;

    items.push({
      label: group.label,
      access: hasRead && hasWrite ? "Read / Write" : hasWrite ? "Write" : "Read",
    });
    [...readScopes, ...writeScopes].forEach((scope) => covered.add(scope));
  }

  // Scopes not represented by any group (future additions) fall back to raw display.
  for (const scope of scopes) {
    if (!covered.has(scope)) {
      items.push({ label: scope, access: "" });
    }
  }

  return items;
}

interface ApiKeyListItemProps {
  apiKey: ApiKey | ApiKeyWithOwner;
  ownerName?: string | null;
  isRevoking: boolean;
  onRevoke: () => void;
}

export function ApiKeyListItem({ apiKey, ownerName, isRevoking, onRevoke }: ApiKeyListItemProps) {
  const [areScopesVisible, setAreScopesVisible] = useState(false);
  const scopeSummary = useMemo(() => summarizeScopes(apiKey.scopes), [apiKey.scopes]);

  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">{apiKey.name}</p>
          <p className="text-xs text-muted-foreground">{ownerName}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setAreScopesVisible((prev) => !prev)}
            aria-expanded={areScopesVisible}
          >
            {areScopesVisible ? "Hide scopes" : `View scopes (${scopeSummary.length})`}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Revoke ${apiKey.name}`}
            disabled={isRevoking}
            onClick={onRevoke}
          >
            {isRevoking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 text-destructive" />
            )}
          </Button>
        </div>
      </div>
      {areScopesVisible && (
        <div className="flex flex-wrap gap-1.5">
          {scopeSummary.map(({ label, access }) => (
            <Badge key={label} variant="secondary" className="gap-1.5 text-xs font-normal">
              <span className="font-medium">{label}</span>
              {access && <span className="text-muted-foreground">{access}</span>}
            </Badge>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Created {formatDate(apiKey.createdAt)} · Last used {formatDate(apiKey.lastUsedAt)}
        {apiKey.expiresAt ? ` · Expires ${formatDate(apiKey.expiresAt)}` : ""}
      </p>
    </div>
  );
}
